import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Archive, DatabaseBackup, RefreshCw, RotateCcw, Search, ShieldAlert } from "lucide-react";
import { getApiUrl } from "../../config/api";

type BackupStatus = "pending" | "success" | "failed";
type BackupAction = "backup" | "restore_request";

interface BackupLog {
  id: number;
  status: BackupStatus;
  notes?: string | null;
  created_at: string;
  action_type?: BackupAction;
  source_backup_id?: number | null;
  artifact_label?: string | null;
  triggered_by_name?: string | null;
}

const BackupRestore: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [logs, setLogs] = useState<BackupLog[]>([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, success: 0, failed: 0, backups: 0, restore_requests: 0 });
  const [notes, setNotes] = useState("");
  const [restoreNotes, setRestoreNotes] = useState<Record<number, string>>({});
  const [selectedAction, setSelectedAction] = useState<"all" | BackupAction>("all");
  const [selectedStatus, setSelectedStatus] = useState<"all" | BackupStatus>("all");
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadLogs = async () => {
    const response = await axios.get(getApiUrl("superadmin/backups"), {
      headers,
      params: {
        action: selectedAction === "all" ? undefined : selectedAction,
        status: selectedStatus === "all" ? undefined : selectedStatus
      }
    });
    setLogs(response.data?.logs || []);
    setSummary(response.data?.summary || { total: 0, pending: 0, success: 0, failed: 0, backups: 0, restore_requests: 0 });
  };

  useEffect(() => {
    loadLogs();
  }, [selectedAction, selectedStatus]);

  const filteredLogs = useMemo(
    () => logs.filter((log) => `${log.artifact_label || ""} ${log.notes || ""} ${log.triggered_by_name || ""}`.toLowerCase().includes(search.toLowerCase())),
    [logs, search]
  );

  const triggerBackup = async () => {
    try {
      setSubmitting(true);
      setNotice("");
      await axios.post(getApiUrl("superadmin/backups"), { notes }, { headers });
      setNotes("");
      setNotice("Backup queued.");
      await loadLogs();
    } catch (error: any) {
      setNotice(error?.response?.data?.message || "Failed to queue backup.");
    } finally {
      setSubmitting(false);
    }
  };

  const requestRestore = async (backupId: number) => {
    try {
      setSubmitting(true);
      setNotice("");
      await axios.post(getApiUrl(`superadmin/backups/${backupId}/restore-request`), { notes: restoreNotes[backupId] || "" }, { headers });
      setRestoreNotes((current) => ({ ...current, [backupId]: "" }));
      setNotice(`Restore request submitted for backup #${backupId}.`);
      await loadLogs();
    } catch (error: any) {
      setNotice(error?.response?.data?.message || "Failed to create restore request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="mx-auto max-w-[1500px] space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Backup & Restore</p>
              <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Platform recovery workspace</h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                Queue manual backups, review historical backup events, and raise restore requests without running destructive actions from the client.
              </p>
            </div>
            <button onClick={loadLogs} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-6">
          {[
            { label: "Events", value: summary.total, Icon: Archive },
            { label: "Backups", value: summary.backups, Icon: DatabaseBackup },
            { label: "Restore Requests", value: summary.restore_requests, Icon: RotateCcw },
            { label: "Pending", value: summary.pending, Icon: ShieldAlert },
            { label: "Successful", value: summary.success, Icon: Archive },
            { label: "Failed", value: summary.failed, Icon: ShieldAlert }
          ].map(({ label, value, Icon }) => (
            <article key={label} className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">{label}</p>
                  <p className="mt-3 text-2xl font-black text-slate-900">{value}</p>
                </div>
                <div className="rounded-2xl bg-slate-100 p-3 text-slate-700"><Icon size={18} /></div>
              </div>
            </article>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-black text-slate-900">Queue backup</h2>
            <p className="mt-2 text-sm text-slate-500">Create a new backup event and log context for the operation.</p>
            <textarea
              className="mt-5 min-h-[180px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
              placeholder="Add notes for this backup window, deployment state, or incident context."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <button onClick={triggerBackup} disabled={submitting} className="mt-4 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">
              {submitting ? "Submitting..." : "Trigger Backup"}
            </button>
            <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
              Restore actions are intentionally request-based. This screen records operator intent and the target backup without executing a destructive restore in-browser.
            </div>
          </article>

          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-900">Recovery history</h2>
                <p className="text-sm text-slate-500">Filter backup events and restore requests across the platform.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <select value={selectedAction} onChange={(e) => setSelectedAction(e.target.value as any)} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none">
                  <option value="all">All actions</option>
                  <option value="backup">Backups</option>
                  <option value="restore_request">Restore requests</option>
                </select>
                <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value as any)} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none">
                  <option value="all">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
            <div className="relative mt-5">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search artifact, note, or operator" className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-12 py-3 text-sm outline-none" />
            </div>
            <div className="mt-5 space-y-4">
              {filteredLogs.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">No backup or restore events found for the current filters.</div>
              ) : (
                filteredLogs.map((log) => (
                  <article key={log.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-black text-slate-900">{log.artifact_label || `event-${log.id}`}</span>
                          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                            {(log.action_type || "backup").replace("_", " ")}
                          </span>
                          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                            {log.status}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-slate-400">
                          {new Date(log.created_at).toLocaleString()} · {log.triggered_by_name || "Unknown operator"}
                          {log.source_backup_id ? ` · source #${log.source_backup_id}` : ""}
                        </div>
                        {log.notes && <p className="mt-3 text-sm leading-6 text-slate-700">{log.notes}</p>}
                      </div>
                    </div>
                    {(log.action_type || "backup") === "backup" && (
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                        <label className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">Restore request notes</label>
                        <textarea
                          rows={3}
                          value={restoreNotes[log.id] || ""}
                          onChange={(e) => setRestoreNotes((current) => ({ ...current, [log.id]: e.target.value }))}
                          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none"
                          placeholder="Explain why this backup should be restored and what environment it applies to."
                        />
                        <button onClick={() => requestRestore(log.id)} disabled={submitting} className="mt-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-60">
                          Request Restore
                        </button>
                      </div>
                    )}
                  </article>
                ))
              )}
            </div>
          </article>
        </section>

        {notice && <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">{notice}</div>}
      </div>
    </div>
  );
};

export default BackupRestore;
